/**
 * Based on FieldFile Component
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import { FIELD_TYPE } from 'Component/Field/Field.config';
import Loader from 'Component/Loader';
import { EventsType, FieldAttrType, FileType } from 'Type/Field.type';
import { formatBytes } from 'Util/Common';

import './FieldFileAdvanced.style';

/** @namespace PeggysagePwa/Component/FieldFileAdvanced/Component */
export class FieldFileAdvancedComponent extends PureComponent {
    static propTypes = {
        attr: FieldAttrType.isRequired,
        events: EventsType.isRequired,
        setRef: PropTypes.func.isRequired,
        description: PropTypes.string.isRequired,
        file: FileType,
        fileData: PropTypes.string.isRequired,
        isLoading: PropTypes.bool.isRequired,
        isMobile: PropTypes.bool.isRequired,
        maxAllowedSize: PropTypes.number.isRequired,
        isAllowedTypesVisible: PropTypes.bool.isRequired,
        resetInputFile: PropTypes.func.isRequired,
        onCaptureButtonClick: PropTypes.func.isRequired,
        onUploadButtonClick: PropTypes.func.isRequired
    };

    static defaultProps = {
        file: {}
    };

    renderAllowedTypes() {
        const {
            attr: { accept = '' } = {}, isAllowedTypesVisible
        } = this.props;

        if (!isAllowedTypesVisible) {
            return null;
        }

        const allowedFieldTypes = (accept || '')
            .split(',')
            .map((type = '') => type.split('/').slice(-1)[0])
            .join(', ');

        if (allowedFieldTypes.length === 0) {
            return null;
        }

        return (
            <p block="FieldFileAdvanced" elem="AllowedTypes">
                { __('Compatible file extensions to upload:') }
                <strong>{ ` ${allowedFieldTypes}` }</strong>
            </p>
        );
    }

    renderMaxAllowedSize() {
        const { maxAllowedSize, isAllowedTypesVisible } = this.props;

        if (!isAllowedTypesVisible) {
            return null;
        }

        return (
            <p block="FieldFileAdvanced" elem="MaxAllowedSize">
                { __('Max allowed file size:') }
                <strong>{ ` ${ formatBytes(maxAllowedSize) }` }</strong>
            </p>
        );
    }

    renderDescription() {
        const { description } = this.props;

        if (!description) {
            return null;
        }

        return (
            <div block="FieldFileAdvanced" elem="Description">
                <p>{ description }</p>
            </div>
        );
    }

    renderOverviewFile() {
        const {
            file: {
                name: fileName,
                type: fileType
            } = {},
            fileData = ''
        } = this.props;

        const format = fileType?.split('/')[0];
        const extension = fileType?.split('/').slice(-1)[0];

        if (format === 'image' && fileData) {
            return (
                <div block="FieldFileAdvanced" elem="OverviewPicture">
                    <img src={ fileData } alt={ fileName } />
                </div>
            );
        }

        return (
            <div block="FieldFileAdvanced" elem="OverviewFile">
                <span
                  block="FieldFileAdvanced"
                  elem="OverviewFileType"
                  mods={ { type: format, ext: extension } }
                >
                    <span>{ extension }</span>
                </span>
                <span block="FieldFileAdvanced" elem="OverviewFileName">
                    { fileName }
                </span>
            </div>
        );
    }

    renderOverviewEmpty() {
        return (
            <div block="FieldFileAdvanced" elem="OverviewEmpty">
                <div block="FieldFileAdvanced" elem="OverviewEmptyInner">
                    <span />
                </div>
            </div>
        );
    }

    renderOverview() {
        const {
            file: {
                name: fileName = ''
            } = {},
            attr: { id = '' } = {}
        } = this.props;

        return (
            <div block="FieldFileAdvanced" elem="Overview">
                <label htmlFor={ id }>
                    { fileName && this.renderOverviewFile() }
                    { !fileName && this.renderOverviewEmpty() }
                </label>
            </div>
        );
    }

    renderCaptureButton() {
        const { isMobile, onCaptureButtonClick } = this.props;

        if (!isMobile) {
            return null;
        }

        return (
            <div block="FieldFileAdvanced" elem="CaptureButton">
                <button
                  type="button"
                  className="Button"
                  onClick={ onCaptureButtonClick }
                  onKeyDown={ onCaptureButtonClick }
                >
                    { __('Take a picture') }
                </button>
                <p>{ __('or') }</p>
            </div>
        );
    }

    renderUploadButton() {
        const { onUploadButtonClick } = this.props;

        return (
            <div block="FieldFileAdvanced" elem="UploadButton">
                <button
                  type="button"
                  className="Button Button_isHollow"
                  onClick={ onUploadButtonClick }
                  onKeyDown={ onUploadButtonClick }
                >
                    { __('Choose a document') }
                </button>
            </div>
        );
    }

    renderModifyButton() {
        const { resetInputFile } = this.props;

        return (
            <button
              type="button"
              block="Button"
              mix={ { block: 'FieldFileAdvanced', elem: 'ModifyButton' } }
              mods={ { likeLink: true } }
              onClick={ resetInputFile }
            >
                { __('Modify my picture') }
            </button>
        );
    }

    renderSubmit() {

    }

    renderButtons() {
        const {
            file: {
                name: fileName = ''
            } = {}
        } = this.props;

        if (fileName) {
            return (
                <div block="FieldFileAdvanced" elem="Buttons">
                    { this.renderModifyButton() }
                    { this.renderSubmit() }
                </div>
            );
        }

        return (
            <div block="FieldFileAdvanced" elem="Buttons">
                { this.renderCaptureButton() }
                { this.renderUploadButton() }
            </div>
        );
    }

    renderInput() {
        const {
            attr = {},
            events = {},
            setRef
        } = this.props;

        return (
            <div block="FieldFileAdvanced" elem="InputWrapper" mods={ { isHidden: true } }>
                <input
                  ref={ setRef }
                  type={ FIELD_TYPE.file }
                  // eslint-disable-next-line @scandipwa/scandipwa-guidelines/jsx-no-props-destruction
                  { ...attr }
                  // eslint-disable-next-line @scandipwa/scandipwa-guidelines/jsx-no-props-destruction
                  { ...events }
                />
            </div>
        );
    }

    render() {
        const {
            isLoading = false
        } = this.props;

        return (
            <div block="FieldFileAdvanced">
                <Loader isLoading={ isLoading } />
                { this.renderOverview() }
                { this.renderDescription() }
                { this.renderInput() }
                <div block="FieldFileAdvanced" elem="Actions">
                    { this.renderAllowedTypes() }
                    { this.renderMaxAllowedSize() }
                    { this.renderButtons() }
                </div>
            </div>
        );
    }
}

export default FieldFileAdvancedComponent;
